import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createLoop,
  deleteLoop,
  fetchLoops,
  transitionLoop,
  updateLoop,
  createManagedHermesAgent,
  createManagedN8nAgent,
  deleteManagedHermesAgent,
  deleteManagedN8nAgent,
  fetchCatalog,
  fetchCommandCenterSummary,
  fetchFleetNodes,
  fetchSkillContent,
  fetchSkills,
  fetchHeatmap,
  fetchN8nLive,
  fetchNodeRegistry,
  fetchRunDetail,
  fetchRuns,
} from './index';
import { COMMAND_CENTER_QUERY_KEYS } from './queryKeys';
import type { ListLoopsFilters, ListRunsFilters, LoopState, UpdateLoopInput } from './types';

// Each resource gets its own query on purpose: a failing runs list must not hide the fleet or the catalog,
// and a slow heatmap aggregate must not stop the board from drawing.

export const useCatalog = (enabled = true) =>
  useQuery({
    queryKey: COMMAND_CENTER_QUERY_KEYS.catalog(),
    queryFn: fetchCatalog,
    enabled,
    retry: false,
  });

// Kept separate from the catalog so an n8n outage never blanks the board; its result is merged in client-side.
export const useN8nLive = (enabled = true) =>
  useQuery({
    queryKey: COMMAND_CENTER_QUERY_KEYS.n8nLive(),
    queryFn: fetchN8nLive,
    enabled,
    staleTime: 30_000,
    retry: false,
  });

export const useHeatmap = (windowHours = 24, enabled = true) =>
  useQuery({
    queryKey: COMMAND_CENTER_QUERY_KEYS.heatmap(windowHours),
    queryFn: () => fetchHeatmap(windowHours),
    enabled,
    retry: false,
  });

export const useCommandCenterSummary = (enabled = true) =>
  useQuery({
    queryKey: COMMAND_CENTER_QUERY_KEYS.summary(),
    queryFn: fetchCommandCenterSummary,
    enabled,
    retry: false,
  });

export const useFleetNodes = (enabled = true) =>
  useQuery({
    queryKey: COMMAND_CENTER_QUERY_KEYS.nodes(),
    queryFn: fetchFleetNodes,
    enabled,
    retry: false,
  });

export const useSkills = (enabled = true) =>
  useQuery({
    queryKey: COMMAND_CENTER_QUERY_KEYS.skills(),
    queryFn: fetchSkills,
    enabled,
    retry: false,
  });

/** One skill's manifest; held until the operator opens a card, so the board itself stays cheap. */
export const useSkillContent = (skillId: string | null) =>
  useQuery({
    queryKey: COMMAND_CENTER_QUERY_KEYS.skillContent(skillId ?? ''),
    queryFn: () => fetchSkillContent(skillId as string),
    enabled: skillId !== null,
    retry: false,
  });

export const useNodeRegistry = (enabled = true) =>
  useQuery({
    queryKey: COMMAND_CENTER_QUERY_KEYS.nodeRegistry(),
    queryFn: fetchNodeRegistry,
    enabled,
    retry: false,
  });

/** Adds a Hermes catalog card without changing any node assignment or desired state. */
export const useCreateManagedHermesAgent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createManagedHermesAgent,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: COMMAND_CENTER_QUERY_KEYS.catalog() }),
  });
};

/** Removes a managed Hermes catalog card without changing the source skill or any node state. */
export const useDeleteManagedHermesAgent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteManagedHermesAgent,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: COMMAND_CENTER_QUERY_KEYS.catalog() }),
  });
};

/** Adds a live n8n workflow as a monitoring card without modifying the workflow. */
export const useCreateManagedN8nAgent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createManagedN8nAgent,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: COMMAND_CENTER_QUERY_KEYS.catalog() }),
  });
};

/** Removes a managed n8n card without changing its workflow or execution history. */
export const useDeleteManagedN8nAgent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteManagedN8nAgent,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: COMMAND_CENTER_QUERY_KEYS.catalog() }),
  });
};

export const useRuns = (filters: ListRunsFilters = {}, enabled = true) =>
  useQuery({
    queryKey: COMMAND_CENTER_QUERY_KEYS.runs(filters),
    queryFn: () => fetchRuns(filters),
    enabled,
    retry: false,
  });

export const useRunDetail = (runId: string, enabled = true) =>
  useQuery({
    queryKey: COMMAND_CENTER_QUERY_KEYS.runDetail(runId),
    queryFn: () => fetchRunDetail(runId),
    enabled: enabled && runId.length > 0,
    retry: false,
  });

/** The loop registry as the board reads it. */
export const useLoops = (enabled: boolean, filters: ListLoopsFilters = {}) =>
  useQuery({
    queryKey: COMMAND_CENTER_QUERY_KEYS.loops(filters),
    queryFn: () => fetchLoops(filters),
    enabled,
  });

/** Files a planned loop at `idea`. Nothing is registered or scheduled by this. */
export const useCreateLoop = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createLoop,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['command-center', 'loops'] }),
  });
};

/** Moves a loop's state. The server refuses an illegal move (400) or a stale one (409). */
export const useTransitionLoop = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ loopId, toState }: { loopId: string; toState: LoopState }) => transitionLoop(loopId, { toState }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['command-center', 'loops'] }),
  });
};

/** Edits a loop's description; state is untouched here. */
export const useUpdateLoop = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ loopId, patch }: { loopId: string; patch: UpdateLoopInput }) => updateLoop(loopId, patch),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['command-center', 'loops'] }),
  });
};

/** Removes a loop filed by mistake; the server refuses once it has left `idea`. */
export const useDeleteLoop = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteLoop,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['command-center', 'loops'] }),
  });
};
